// Author: Akarsh Singh | 2026-09-21 | Bounded, session-scoped read snapshots; never cache permission decisions.
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Subject } from 'rxjs';

interface Entry {
  subject: BehaviorSubject<any>;
  updatedAt: number;
  inFlight?: Promise<any>;
}

@Injectable({ providedIn: 'root' })
export class ServiceCompletionReviewSWRService {
  readonly invalidated$ = new Subject<void>();
  private cache = new Map<string, Entry>();
  private epoch = 0;
  constructor(private cookies: CookieService) {}

  keyOf(dispatchId: number, invoiceId: number | null): string {
    return JSON.stringify([
      ...['userId', 'userRole', 'roleId', 'companyId', 'clientId', 'tokenId', 'tokenHash']
        .map(name => this.cookies.get(name)), dispatchId, invoiceId,
    ]);
  }

  private entry(key: string): Entry {
    let entry = this.cache.get(key);
    if (!entry) {
      if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
      entry = { subject: new BehaviorSubject(null), updatedAt: 0 };
      this.cache.set(key, entry);
    }
    return entry;
  }

  getCached(key: string): any {
    const entry = this.cache.get(key);
    return entry && Date.now() - entry.updatedAt < 15000 ? this.clone(entry.subject.value) : null;
  }

  refresh(key: string, fetcher: () => Promise<any>): Promise<any> {
    const entry = this.entry(key);
    if (entry.inFlight) return entry.inFlight.then(data => this.clone(data));
    const epoch = this.epoch;
    const task = Promise.resolve().then(fetcher).then(data => {
      if (epoch !== this.epoch || this.cache.get(key) !== entry) return undefined;
      entry.updatedAt = Date.now();
      entry.subject.next(this.clone(data));
      return data;
    }).finally(() => { if (entry.inFlight === task) entry.inFlight = undefined; });
    entry.inFlight = task;
    return task.then(data => this.clone(data));
  }

  invalidateAll(): void { this.epoch++; this.cache.clear(); this.invalidated$.next(); }

  isMutation(url: string, method: string): boolean {
    if (/^(GET|HEAD|OPTIONS)$/i.test(method)) return false;
    const path = url.split('?')[0];
    // Includes supplier completion, buyer feedback and related invoice/header edits.
    if (!/\/services\/(rfq|serviceRfq)\//i.test(path)) return false;
    return !/^(get|fetch|search|filter|generate|export|verify)/i.test(path.split('/').pop() || '');
  }

  private clone(data: any): any { return data == null ? data : JSON.parse(JSON.stringify(data)); }
}
